import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { imgUrl, money, SWATCH } from "../lib/api";

const CATEGORY_NOUN = {
  rings: "Ring",
  "ring-bands": "Ring Band",
  necklaces: "Necklace",
  earrings: "Earrings",
  bracelets: "Bracelet",
};

function subtitleFor(product) {
  if (product.card_subtitle && product.card_subtitle.trim()) return product.card_subtitle;
  const noun = CATEGORY_NOUN[product.category] || (product.category_label || "Piece");
  const shape = product.shape && product.shape_confident ? product.shape : null;
  return shape ? `${shape} Diamond ${noun}` : `Diamond ${noun}`;
}

const PREFERRED_METAL_ORDER = ["Gold", "18K Yellow Gold", "Yellow Gold", "14K Yellow Gold", "Rose Gold", "White Gold", "Silver"];

function pickDefaultColor(product) {
  const colors = product.variants?.map((v) => v.color) || [];
  if (product.default_color && colors.includes(product.default_color)) return product.default_color;
  return PREFERRED_METAL_ORDER.find((c) => colors.includes(c)) || colors[0];
}

export const ProductCard = ({ product, index = 0 }) => {
  const colors = product.variants?.map((v) => v.color) || [];
  const [active, setActive] = useState(pickDefaultColor(product));
  const variant = product.variants?.find((v) => v.color === active) || product.variants?.[0];
  const image = variant?.images?.[0];
  const subtitle = subtitleFor(product);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min((index % 6) * 0.05, 0.3), ease: "easeOut" }}
      data-testid={`product-card-${product.id}`}
      className="group"
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden bg-[#FFFFFF] aspect-[4/5]">
          {image ? (
            <img
              src={imgUrl(image)}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-contain p-3 transition-transform duration-700 ease-out group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs">
              No image
            </div>
          )}
          <span className="absolute top-4 left-4 label-track text-[10px] text-[#A67C3D]">
            {product.category_label || product.category}
          </span>
        </div>
      </Link>

      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <Link to={`/product/${product.id}`}>
            <h3 className="font-heading text-xl text-[#0A0A0A] leading-tight">{product.name}</h3>
          </Link>
          <p
            className="text-[#525252] text-xs mt-1 leading-snug truncate max-w-[26ch]"
            data-testid={`product-subtitle-${product.id}`}
          >
            {subtitle}
          </p>
          <p className="text-[#737373] text-sm mt-1">From {money(product.price)}</p>
        </div>
        <div className="flex items-center gap-2 pt-1.5">
          {colors.map((c) => (
            <button
              key={c}
              data-testid={`card-swatch-${product.id}-${c.replace(/\s/g, "")}`}
              onMouseEnter={() => setActive(c)}
              onClick={(e) => {
                e.preventDefault();
                setActive(c);
              }}
              title={c}
              className={`w-4 h-4 rounded-full transition-transform ${
                active === c ? "ring-1 ring-offset-2 ring-offset-[#FFFFFF] ring-[#A67C3D] scale-110" : ""
              }`}
              style={{ backgroundColor: SWATCH[c] || "#888" }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};
